// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { Stack, useGlobalSearchParams, useRouter, useSegments } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { useColorScheme } from '@/components/useColorScheme';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { HeaderUser } from '@/components/HeaderUser';
import { BottomMenu } from '@/components/BottomMenu';
import { getCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';
import { useDebugState } from '@/stores/debugState';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useAuthFlow } from '@/app/layout/useAuthFlow';
import { useServiceHealth } from '@/app/layout/useServiceHealth';
import { useUserInfoSync } from '@/app/layout/useUserInfoSync';
import { ServiceStatusModal } from '@/app/layout/ServiceStatusModal';
import { DebugModal } from '@/app/layout/DebugModal';
import FaviconImage from '@/assets/images/favicon.png';
import { createAppLogger } from '@/services/appLogger';

export default function RootLayoutNav() {
  const colorScheme = useColorScheme();
  const { t } = useI18n();
  const { user, setUser } = useUser();
  const router = useRouter();
  const segments = useSegments();
  const { debug: debugParam } = useGlobalSearchParams<{ debug?: string }>();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugTourName, setDebugTourName] = useState<string | null>(null);
  const [debugRoundName, setDebugRoundName] = useState<string | null>(null);
  const [debugPlayerId, setDebugPlayerId] = useState<string | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const roundId = useRoundState((state) => state.roundId);
  const debugEnabled = useDebugState((state) => state.enabled);
  const setDebugEnabled = useDebugState((state) => state.setEnabled);
  const [tourId, setTourId] = useState<string | null>(null);
  const [mysteryMenuEnabled, setMysteryMenuEnabled] = useState(false);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'frontend-error',
      }),
    [apiBaseUrl, user?.token]
  );

  const { authChecked } = useAuthFlow({ user, setUser });

  const {
    canCheckServiceHealth,
    serviceStatusOpen,
    setServiceStatusOpen,
    serviceStatusMissing,
    serviceStatusCheckedAt,
    serviceStatusHasWarnings,
    serviceStatusFetchFailed,
    serviceStatusFlashOn,
    serviceStatusWarnings,
  } = useServiceHealth({ apiBaseUrl, user });

  useEffect(() => {
    const raw = Array.isArray(debugParam) ? debugParam[0] : debugParam;
    if (raw === 'true' || raw === '1') {
      setDebugEnabled(true);
    }
  }, [debugParam, setDebugEnabled]);

  useEffect(() => {
    setTourId(getCurrentTourId() ?? null);
  }, [segments]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const formatError = (err: unknown) => {
      if (err instanceof Error) {
        return { message: err.message, stack: err.stack };
      }
      if (typeof err === 'string') {
        return { message: err };
      }
      try {
        return { message: JSON.stringify(err) };
      } catch {
        return { message: String(err) };
      }
    };

    const handleError = (event: ErrorEvent) => {
      const details = formatError(event.error ?? event.message);
      logToBff(
        'frontend.unhandled.error',
        {
          ...details,
          source: event.filename,
          line: event.lineno,
          column: event.colno,
          userId: user?.id,
          path: window.location?.pathname ?? '',
          search: window.location?.search ?? '',
          href: window.location?.href ?? '',
          userAgent: navigator?.userAgent ?? '',
        },
        'error'
      );
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const details = formatError(event.reason);
      logToBff(
        'frontend.unhandled.rejection',
        {
          ...details,
          userId: user?.id,
          path: window.location?.pathname ?? '',
          search: window.location?.search ?? '',
          href: window.location?.href ?? '',
          userAgent: navigator?.userAgent ?? '',
        },
        'error'
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [logToBff, user?.id]);

  useEffect(() => {
    if (!roundId || !user?.token) {
      setMysteryMenuEnabled(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [roundRes, defsRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
          fetch(`${apiBaseUrl}/api/game-definitions`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
        ]);
        if (!roundRes.ok || !defsRes.ok) {
          if (!cancelled) setMysteryMenuEnabled(false);
          return;
        }
        const roundPayload = (await roundRes.json()) as {
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
        };
        const defsPayload = (await defsRes.json()) as Array<{ id?: string; name?: string }>;
        const ids = roundPayload.gameDefinitionIds ?? roundPayload.GameDefinitionIds ?? [];
        const selected = new Set(ids);
        const hasMystery = defsPayload.some(
          (def) =>
            def?.id &&
            selected.has(def.id) &&
            typeof def.name === 'string' &&
            def.name.toLowerCase() === 'mystery'
        );
        if (!cancelled) setMysteryMenuEnabled(hasMystery);
      } catch {
        if (!cancelled) setMysteryMenuEnabled(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, roundId, user?.token]);

  useEffect(() => {
    if (!debugEnabled || !user?.token) {
      setDebugTourName(null);
      setDebugRoundName(null);
      setDebugPlayerId(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setDebugLoading(true);
      try {
        const requests: Array<Promise<Response>> = [];
        if (user.id) {
          requests.push(
            fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        if (tourId) {
          requests.push(
            fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        if (roundId) {
          requests.push(
            fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        const responses = await Promise.all(requests);
        let resolvedTourName: string | null = null;
        let resolvedRoundName: string | null = null;
        let resolvedPlayerId: string | null = null;
        for (const response of responses) {
          if (!response.ok) continue;
          const payload = (await response.json()) as { name?: string; tourName?: string; courseName?: string; id?: string };
          if (payload.id && !resolvedPlayerId) {
            resolvedPlayerId = payload.id;
          }
          if (payload.tourName || payload.name) {
            resolvedTourName = payload.tourName ?? payload.name ?? null;
          }
          if (payload.courseName || payload.name) {
            resolvedRoundName = payload.courseName ?? payload.name ?? null;
          }
        }
        if (!cancelled) {
          setDebugTourName(resolvedTourName);
          setDebugRoundName(resolvedRoundName);
          setDebugPlayerId(resolvedPlayerId);
        }
      } catch {
        if (!cancelled) {
          setDebugTourName(null);
          setDebugRoundName(null);
          setDebugPlayerId(null);
        }
      } finally {
        if (!cancelled) setDebugLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, debugEnabled, roundId, tourId, user?.id, user?.token]);

  const buildRequestId = useCallback(() => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }
    return `req-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }, []);

  useUserInfoSync({ apiBaseUrl, user, setUser, buildRequestId });

  if (!authChecked) {
    return null;
  }

  const showBottomMenu = segments[0] !== 'login';
  const selectPlayTourId = getCurrentTourId();
  const selectPlayRoundId = roundId;
  const canOpenSelectPlay = !!(selectPlayRoundId || selectPlayTourId);
  const selectPlayPath = selectPlayRoundId
    ? `/round/${selectPlayRoundId}/select-games`
    : selectPlayTourId
      ? `/tour/${selectPlayTourId}/round-init`
      : '/tournaments';

  return (
    <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
      <View style={styles.shell}>
        <View style={styles.stack}>
          <ServiceStatusModal
            open={serviceStatusOpen}
            onClose={() => setServiceStatusOpen(false)}
            missing={serviceStatusMissing}
            checkedAt={serviceStatusCheckedAt}
            hasWarnings={serviceStatusHasWarnings}
            warnings={serviceStatusWarnings}
          />
          <DebugModal
            open={debugOpen}
            onClose={() => setDebugOpen(false)}
            user={user}
            playerId={debugPlayerId}
            roundId={roundId}
            tourId={tourId}
            roundName={debugRoundName}
            tourName={debugTourName}
            loading={debugLoading}
            onDisable={() => {
              setDebugEnabled(false);
              setDebugOpen(false);
            }}
          />
          <Modal transparent visible={leftMenuOpen} animationType="fade" onRequestClose={() => setLeftMenuOpen(false)}>
            <LeftMenu
              open={leftMenuOpen}
              canOpenSelectPlay={canOpenSelectPlay}
              selectPlayPath={selectPlayPath}
              mysteryEnabled={mysteryMenuEnabled}
              mysteryPath={roundId ? `/round/${roundId}/mystery-prize` : '/round'}
              onClose={() => setLeftMenuOpen(false)}
            />
          </Modal>
          <Stack
            screenOptions={{
              headerLeft: () => (
                <Pressable style={styles.headerIconButton} onPress={() => setLeftMenuOpen((prev) => !prev)}>
                  <Image source={FaviconImage} style={styles.headerIcon} />
                </Pressable>
              ),
              headerRight: () => (
                <View style={styles.headerRight}>
                  {debugEnabled ? (
                    <Pressable
                      style={styles.debugButton}
                      onPress={() => setDebugOpen(true)}
                      accessibilityLabel={t('debug.buttonLabel')}
                    >
                      <Text style={styles.debugButtonText}>{t('debug.button')}</Text>
                    </Pressable>
                  ) : null}
                  {canCheckServiceHealth ? (
                    serviceStatusMissing.length > 0 || serviceStatusFetchFailed ? (
                      <Pressable
                        style={[
                          styles.serviceStatusButtonCritical,
                          serviceStatusFetchFailed && !serviceStatusFlashOn ? styles.serviceStatusButtonCriticalDim : null,
                        ]}
                        onPress={() => setServiceStatusOpen(true)}
                        accessibilityLabel={t('serviceStatus.buttonLabel')}
                      >
                        <Text style={styles.serviceStatusButtonText}>{t('serviceStatus.alert')}</Text>
                      </Pressable>
                    ) : serviceStatusHasWarnings ? (
                      <Pressable
                        style={styles.serviceStatusButtonWarning}
                        onPress={() => setServiceStatusOpen(true)}
                        accessibilityLabel={t('serviceStatus.buttonLabel')}
                      >
                        <Text style={styles.serviceStatusButtonText}>{t('serviceStatus.alert')}</Text>
                      </Pressable>
                    ) : null
                  ) : null}
                  <HeaderUser />
                </View>
              ),
            }}
          >
            <Stack.Screen name="index" options={{ href: null }} />
            <Stack.Screen name="login" options={{ title: t('auth.loginTitle'), headerShown: false }} />
            <Stack.Screen name="tournaments" options={{ title: t('tournaments.title') }} />
            <Stack.Screen name="modal" options={{ presentation: 'modal', title: t('modal.title') }} />
            <Stack.Screen name="debug/deeplink" options={{ title: t('debug.deeplinkTitle') }} />
            <Stack.Screen name="round/[roundId]/select-games" options={{ title: t('selectGames.title') }} />
            <Stack.Screen name="round/[roundId]/mystery-prize" options={{ title: t('mysteryPrize.title') }} />
            <Stack.Screen name="round/[roundId]/team-select" options={{ title: t('teamDraw.title') }} />
            <Stack.Screen name="round/[roundId]/hole/[hole]/index" options={{ title: t('hole.title') }} />
            <Stack.Screen name="round/[roundId]/hole/[hole]/score" options={{ title: t('hole.title') }} />
            <Stack.Screen name="round/[roundId]/hole/[hole]/compact" options={{ title: t('hole.title') }} />
            <Stack.Screen name="tour/[tourId]/leaderboard" options={{ title: t('tourLeaderboard.title') }} />
            <Stack.Screen name="round/[roundId]/summary" options={{ title: t('roundResults.title') }} />
            <Stack.Screen name="tour/[tourId]/round-init" options={{ title: t('players.title') }} />
            <Stack.Screen name="round/[roundId]/scorecard" options={{ title: t('scorecard.title') }} />
            <Stack.Screen name="tour/[tourId]/edit" options={{ title: t('tourEdit.breadcrumbTitle') }} />
            <Stack.Screen name="profile/index" options={{ title: t('profile.title') }} />
            <Stack.Screen name="config-course" options={{ title: t('courseConfig.title') }} />
          </Stack>
        </View>
        {showBottomMenu ? <BottomMenu /> : null}
      </View>
    </ThemeProvider>
  );
}

type LeftMenuProps = {
  open: boolean;
  canOpenSelectPlay: boolean;
  selectPlayPath: string;
  mysteryEnabled: boolean;
  mysteryPath: string;
  onClose: () => void;
};

const LeftMenu = ({ open, canOpenSelectPlay, selectPlayPath, mysteryEnabled, mysteryPath, onClose }: LeftMenuProps) => {
  const router = useRouter();
  const { t } = useI18n();

  if (!open) return null;

  return (
    <Pressable style={styles.leftMenuBackdrop} onPress={onClose}>
      <View style={styles.leftMenuCard}>
        <Pressable
          style={[styles.leftMenuItem, !canOpenSelectPlay && styles.leftMenuItemDisabled]}
          disabled={!canOpenSelectPlay}
          onPress={() => {
            onClose();
            router.push(selectPlayPath);
          }}
        >
          <Text style={[styles.leftMenuItemText, !canOpenSelectPlay && styles.leftMenuItemTextDisabled]}>
            {t('menu.selectGames')}
          </Text>
        </Pressable>
        {mysteryEnabled ? (
          <Pressable
            style={styles.leftMenuItem}
            onPress={() => {
              onClose();
              router.push(mysteryPath);
            }}
          >
            <Text style={styles.leftMenuItemText}>{t('mysteryPrize.title')}</Text>
          </Pressable>
        ) : null}
        <Pressable
          style={styles.leftMenuItem}
          onPress={() => {
            onClose();
            router.push('/tournaments');
          }}
        >
          <Text style={styles.leftMenuItemText}>{t('menu.tour')}</Text>
        </Pressable>
        <Pressable
          style={styles.leftMenuItem}
          onPress={() => {
            onClose();
            router.push('/config-course');
          }}
        >
          <Text style={styles.leftMenuItemText}>{t('menu.configCourse')}</Text>
        </Pressable>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: '#0c1429',
  },
  stack: {
    flex: 1,
  },
  headerIcon: {
    width: 40,
    height: 40,
    marginLeft: 12,
  },
  headerIconButton: {
    padding: 4,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  serviceStatusButtonCritical: {
    backgroundColor: '#d7263d',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#f08a95',
  },
  serviceStatusButtonCriticalDim: {
    opacity: 0.35,
  },
  serviceStatusButtonWarning: {
    backgroundColor: '#e68a00',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#f1c27a',
  },
  serviceStatusButtonText: {
    color: '#fff3f4',
    fontWeight: '700',
  },
  debugButton: {
    backgroundColor: '#1b2a4a',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2a3a6b',
  },
  debugButtonText: {
    color: '#cfe1ff',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  leftMenuBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(7,12,24,0.35)',
  },
  leftMenuCard: {
    position: 'absolute',
    top: 52,
    left: 12,
    backgroundColor: '#0b1224',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#24346b',
    minWidth: 170,
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },
  leftMenuItem: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  leftMenuItemDisabled: {
    opacity: 0.45,
  },
  leftMenuItemText: {
    color: '#e5edff',
    fontWeight: '600',
    fontSize: 14,
  },
  leftMenuItemTextDisabled: {
    color: '#7b86a3',
  },
});
